import { gql, useQuery } from '@apollo/client';
import AddressAutocomplete from '@orn/shared-web/components/AddressAutocomplete';
import Button, { styles as buttonStyles } from '@orn/shared-web/components/Button/Button';
import Input, { Label, PhoneNumberInput, usePhoneNumberInput } from '@orn/shared-web/components/Input';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip';
import { CheckIcon, MailIcon } from '@orn/shared-web/icons';
import logger from '@orn/shared-web/lib/logger';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { ResidenceType } from '../../../__generated__/globalTypes';
import { useDeleteAccount } from '../../../hooks/mutations/useDeleteAccount';
import { useUpdateUser } from '../../../hooks/mutations/useUpdateUser';
import { useUpsertResidence } from '../../../hooks/mutations/useUpsertResidence';
import { getOptionalEnv } from '../../../lib/getEnv';
import PhoneVerificationModal from '../../PhoneVerificationModal';

import DeleteAccountModal from './DeleteAccountModal';
import { diffPersonalInfo, hasUpdateUserChanges } from './personalInfoDiff';

import type {
  GetPersonalSettingsQuery,
  GetPersonalSettingsQueryVariables,
} from './__generated__/PersonalSettingsForm.gql';
import type { IParsedAddress } from '@orn/shared-web/components/AddressAutocomplete';
import type { IPhoneValidationResult } from '@orn/shared-web/components/Input/PhoneNumberInput';

// ---------------------------------------------------------------------------
// Base (presentational) — renders the UI from props + callbacks.
// ---------------------------------------------------------------------------

interface IPersonalSettingsPhone {
  value: string;
  onChange: (value: string) => void;
  validation: IPhoneValidationResult;
  /** True when the entered number differs from the saved one. */
  changed: boolean;
  verifiedAt: string | null;
  hasSavedNumber: boolean;
  onVerify: () => void;
}

interface IPersonalSettingsAddress {
  apiKey: string;
  defaultValue: string;
  onSelect: (address: IParsedAddress) => void;
  onInputChange: (input: string) => void;
  /** Validation error shown after a save attempt; undefined hides it. */
  error?: string;
}

interface IPersonalSettingsVerification {
  isOpen: boolean;
  /** Saved phone number to verify; the modal renders only when truthy. */
  phoneNumber: string;
  onClose: () => void;
  onVerified: () => void;
}

interface IPersonalSettingsDeletion {
  isModalOpen: boolean;
  /** True while the delete mutation is in flight. */
  isDeleting: boolean;
  /** Error shown in the modal after a failed delete; undefined hides it. */
  error?: string;
  onOpenModal: () => void;
  onCloseModal: () => void;
  onConfirm: () => void;
}

export interface IPersonalSettingsFormBaseProps {
  firstName: string;
  lastName: string;
  preferredName: string;
  email: string;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onPreferredNameChange: (value: string) => void;
  /** Validation errors shown after a save attempt; undefined hides the hint. */
  firstNameError?: string;
  lastNameError?: string;
  phone: IPersonalSettingsPhone;
  address: IPersonalSettingsAddress;
  verification: IPersonalSettingsVerification;
  deletion: IPersonalSettingsDeletion;
  isSaving: boolean;
  isSaveDisabled: boolean;
  /** Shows the green "Saved" confirmation; holds until the form goes dirty again. */
  justSaved: boolean;
  saveError?: string;
  onSave: () => void;
}

interface ICardSectionProps {
  label: string;
  children: React.ReactNode;
}

const CardSection = ({ label, children }: ICardSectionProps) => (
  <div className="flex flex-col gap-5">
    <p className="text-quaternary text-xs font-semibold uppercase tracking-wide">{label}</p>
    <div className="flex flex-col gap-4">{children}</div>
  </div>
);

/**
 * Presentational "Personal Information" settings form (Figma node `4198:95847`):
 * name, preferred name, read-only email, phone (with inline verification), and
 * primary address, plus the right-aligned Delete account / Save buttons and the
 * phone-verification and delete-confirmation modals. Fully controlled — holds no
 * data or mutations; the `PersonalSettingsForm` wrapper (below) owns state, the
 * query, and the mutation callbacks.
 */
export const PersonalSettingsFormBase = ({
  firstName,
  lastName,
  preferredName,
  email,
  onFirstNameChange,
  onLastNameChange,
  onPreferredNameChange,
  firstNameError,
  lastNameError,
  phone,
  address,
  verification,
  deletion,
  isSaving,
  isSaveDisabled,
  justSaved,
  saveError,
  onSave,
}: IPersonalSettingsFormBaseProps) => {
  const showVerifyButton = !phone.changed && phone.hasSavedNumber && !phone.verifiedAt;

  return (
    <div className="flex max-w-[736px] flex-col gap-5">
      <div className="bg-primary border-secondary shadow-card overflow-hidden rounded-2xl border">
        <div className="bg-secondary border-secondary flex flex-col gap-0.5 border-b px-5 py-5">
          <p className="text-primary text-lg font-semibold">Personal Information</p>
          <p className="text-tertiary text-sm">Your name and contact details.</p>
        </div>

        <div className="flex flex-col gap-8 px-5 py-6">
          <CardSection label="General info">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="First name"
                value={firstName}
                onChange={onFirstNameChange}
                size="md"
                isDisabled={isSaving}
                isInvalid={Boolean(firstNameError)}
                hint={firstNameError}
              />
              <Input
                label="Last name"
                value={lastName}
                onChange={onLastNameChange}
                size="md"
                isDisabled={isSaving}
                isInvalid={Boolean(lastNameError)}
                hint={lastNameError}
              />
            </div>
            <Input
              label="Preferred name"
              value={preferredName}
              onChange={onPreferredNameChange}
              size="md"
              isDisabled={isSaving}
              hint="We'll address you by this name in the app and emails"
            />
          </CardSection>

          <div className="border-secondary border-t" />

          <CardSection label="Contact & address">
            <Input
              label="Email address"
              icon={MailIcon}
              value={email}
              size="md"
              isDisabled
              isReadOnly
              tooltip="Your email address can't be changed here."
            />

            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1.5">
                <Label isRequired>Phone number</Label>
                <div className="flex items-start gap-2">
                  <PhoneNumberInput
                    label=""
                    value={phone.value}
                    onChange={phone.onChange}
                    hint="For account notifications and recovery."
                    isRequired
                    isDisabled={isSaving}
                    validation={phone.validation}
                    className="flex-1"
                  />
                  {showVerifyButton && (
                    <Tooltip
                      title="You need to verify your phone number before you can send text messages to Mantle."
                      placement="top"
                    >
                      <TooltipTrigger
                        onPress={phone.onVerify}
                        className={cx(
                          buttonStyles.common.root,
                          buttonStyles.sizes.md.root,
                          buttonStyles.colors.primary.root,
                          'h-11 rounded-lg'
                        )}
                      >
                        <span className="px-0.5">Verify</span>
                      </TooltipTrigger>
                    </Tooltip>
                  )}
                </div>
              </div>
              {phone.changed && phone.validation.isValid && (
                <span className="text-warning-primary text-xs font-medium">
                  You'll need to re-verify your phone number after saving.
                </span>
              )}
              {!phone.changed && phone.verifiedAt && (
                <span className="text-success-primary text-xs font-medium">Verified</span>
              )}
            </div>

            <AddressAutocomplete
              apiKey={address.apiKey}
              label="Address"
              hint={address.error}
              defaultValue={address.defaultValue}
              includedPrimaryTypes={['street_address']}
              isRequired
              isInvalid={Boolean(address.error)}
              isDisabled={isSaving}
              onSelect={address.onSelect}
              onInputChange={address.onInputChange}
            />
          </CardSection>
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:items-end">
        {saveError && <span className="text-error-primary text-sm">{saveError}</span>}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
          <Button
            color="primary-destructive"
            size="md"
            onClick={deletion.onOpenModal}
            isDisabled={isSaving || deletion.isDeleting}
            className="w-full sm:w-auto"
          >
            Delete account
          </Button>
          {justSaved ? (
            // Stays green until the form goes dirty again (no auto-revert timer).
            <Button
              color="primary-success"
              size="md"
              iconLeading={CheckIcon}
              className="pointer-events-none w-full sm:w-auto"
            >
              Saved
            </Button>
          ) : (
            <Button
              color="primary"
              size="md"
              onClick={onSave}
              isLoading={isSaving}
              isDisabled={isSaveDisabled}
              showTextWhileLoading
              className="w-full sm:w-auto"
            >
              {isSaving ? 'Saving' : 'Save changes'}
            </Button>
          )}
        </div>
      </div>

      {verification.phoneNumber && (
        <PhoneVerificationModal
          isOpen={verification.isOpen}
          onClose={verification.onClose}
          onVerified={verification.onVerified}
          phoneNumber={verification.phoneNumber}
        />
      )}

      <DeleteAccountModal
        isOpen={deletion.isModalOpen}
        isDeleting={deletion.isDeleting}
        error={deletion.error}
        onClose={deletion.onCloseModal}
        onConfirm={deletion.onConfirm}
      />
    </div>
  );
};

PersonalSettingsFormBase.displayName = 'PersonalSettingsFormBase';

// ---------------------------------------------------------------------------
// Wrapper (data) — fetches the user + sets up mutations, then renders the base.
// ---------------------------------------------------------------------------

const GET_PERSONAL_SETTINGS = gql`
  query GetPersonalSettings {
    getUser {
      id
      firstName
      lastName
      preferredName
      email
      phoneNumber
      phoneNumberVerifiedAt
      primaryResidence {
        id
        addressLine1
        addressLine2
        city
        state
        postalCode
        country
        formatted
        placeId
        residenceType
        isPrimary
      }
    }
  }
`;

type SettingsUser = NonNullable<GetPersonalSettingsQuery['getUser']>;

const seedAddress = (residence: SettingsUser['primaryResidence']): IParsedAddress | null =>
  residence
    ? {
        formattedAddress: residence.formatted ?? '',
        placeId: residence.placeId ?? '',
        subpremise: residence.addressLine2 ?? undefined,
        city: residence.city,
        state: residence.state ?? undefined,
        postalCode: residence.postalCode ?? undefined,
        country: residence.country,
      }
    : null;

/**
 * "My account" settings form wrapper. Fetches the user's profile + primary
 * residence, owns the editable form state, and implements the save callback
 * that fans out to `updateUser` (name/preferred/phone) and `upsertResidence`
 * (address) via `Promise.allSettled`. Renders `PersonalSettingsFormBase` for UI.
 */
const PersonalSettingsForm = () => {
  const apiKey = getOptionalEnv('VITE_GOOGLE_PLACES_API_KEY') ?? '';

  const {
    data,
    loading: queryLoading,
    refetch,
  } = useQuery<GetPersonalSettingsQuery, GetPersonalSettingsQueryVariables>(GET_PERSONAL_SETTINGS);
  const user = data?.getUser;
  const primaryResidence = user?.primaryResidence ?? null;

  const [updateUser, { loading: updateUserLoading }] = useUpdateUser();
  const [upsertResidence, { loading: upsertResidenceLoading }] = useUpsertResidence();
  const [deleteAccount, { loading: deleteLoading }] = useDeleteAccount();

  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [preferredName, setPreferredName] = React.useState('');
  const [hasAttemptedSave, setHasAttemptedSave] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [justSaved, setJustSaved] = React.useState(false);
  const [showVerificationModal, setShowVerificationModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const [address, setAddress] = React.useState<IParsedAddress | null>(null);
  const [createdResidenceId, setCreatedResidenceId] = React.useState<string | null>(null);

  const {
    value: phoneNumber,
    validation: phoneValidation,
    handleChange: handlePhoneNumberChange,
    setHasTouched: setPhoneNumberTouched,
  } = usePhoneNumberInput(user?.phoneNumber ?? '', true);

  // Seed editable fields once data arrives (and after a save-triggered refetch).
  React.useEffect(() => {
    if (!user) {
      return;
    }
    setFirstName(user.firstName ?? '');
    setLastName(user.lastName ?? '');
    setPreferredName(user.preferredName ?? '');
    setAddress(seedAddress(user.primaryResidence));
  }, [user]);

  const handleAddressInputChange = React.useCallback((next: string) => {
    setAddress((prev) => (prev && prev.formattedAddress === next ? prev : null));
  }, []);

  const e164PhoneNumber = phoneValidation.e164Value || phoneNumber;
  const phoneChanged = e164PhoneNumber !== (user?.phoneNumber ?? '');

  // Address only saves on a fresh, structured street pick. A `null` `address`
  // (partial typing / cleared field) is intentionally not a change — the upsert
  // needs a city/country, so this form updates an address but can't blank one out.
  const addressChanged = Boolean(
    address &&
      (address.placeId !== (primaryResidence?.placeId ?? '') ||
        address.formattedAddress !== (primaryResidence?.formatted ?? ''))
  );

  const updateDiff = diffPersonalInfo(
    {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      preferredName: user?.preferredName ?? '',
      phoneNumber: user?.phoneNumber ?? '',
    },
    {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      // The API rejects empty/whitespace names and can't clear preferredName, so
      // an emptied field falls back to the saved value (treated as no change)
      // rather than sending '' and tripping a validation error.
      preferredName: preferredName.trim() || (user?.preferredName ?? ''),
      phoneNumber: e164PhoneNumber,
    }
  );

  const namesPresent = Boolean(firstName.trim() && lastName.trim());
  const addressValid = Boolean(address && address.formattedAddress.trim());
  const hasChanges = hasUpdateUserChanges(updateDiff) || addressChanged;
  const isFormValid = phoneValidation.isValid && namesPresent && addressValid;
  const isSaving = updateUserLoading || upsertResidenceLoading;

  // Clear the "Saved" confirmation the moment the user edits anything again.
  React.useEffect(() => {
    if (hasChanges) {
      setJustSaved(false);
    }
  }, [hasChanges]);

  const existingResidenceId = createdResidenceId ?? primaryResidence?.id ?? null;

  const saveAddress = async () => {
    if (!address || !user) {
      return;
    }
    const addressLine1 =
      address.streetNumber && address.streetName
        ? `${address.streetNumber} ${address.streetName}`
        : (primaryResidence?.addressLine1 ?? null);

    const result = await upsertResidence({
      variables: {
        input: {
          residenceId: existingResidenceId,
          userId: existingResidenceId ? null : user.id,
          isPrimary: true,
          // No residence-type picker here; default to House so the DB CHECK
          // (`residenceTypeOther` required iff type=OTHER) is satisfied.
          residenceType: ResidenceType.House,
          addressLine1,
          addressLine2: address.subpremise ?? null,
          city: address.city ?? '',
          state: address.state ?? null,
          postalCode: address.postalCode ?? null,
          country: address.country ?? '',
          placeId: address.placeId || null,
          formatted: address.formattedAddress || null,
        },
      },
    });
    const upsertedId = result.data?.upsertResidence.id ?? null;
    if (upsertedId && !existingResidenceId) {
      setCreatedResidenceId(upsertedId);
      // Create path only: `User.primaryResidence` stays null after a normalized
      // cache merge, so refetch to flip the reference to the new row.
      await refetch();
    }
  };

  const handleSave = async () => {
    setHasAttemptedSave(true);
    setPhoneNumberTouched(true);
    setSaveError(null);

    if (!isFormValid) {
      return;
    }

    const tasks: Promise<unknown>[] = [];
    if (hasUpdateUserChanges(updateDiff)) {
      tasks.push(updateUser({ variables: { input: updateDiff } }));
    }
    if (addressChanged) {
      tasks.push(saveAddress());
    }
    if (tasks.length === 0) {
      return;
    }

    const results = await Promise.allSettled(tasks);
    const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (failures.length > 0) {
      failures.forEach((failure) => logger.error('Failed to save personal settings', failure.reason));
      setSaveError("Couldn't save your changes. Please try again.");
      return;
    }
    setJustSaved(true);
  };

  // The mutation tombstones the user and ends the session server-side, so a
  // hard redirect lands on the login screen with no stale Apollo cache. On
  // failure the account still exists, so surface an error and keep the modal open.
  const handleDeleteAccount = async () => {
    setDeleteError(null);
    try {
      await deleteAccount();
      window.location.href = '/';
    } catch (error) {
      logger.error('Failed to delete account', error);
      setDeleteError("Couldn't delete your account. Please try again.");
    }
  };

  if (queryLoading || !user) {
    return <div className="text-secondary">Loading...</div>;
  }

  return (
    <PersonalSettingsFormBase
      firstName={firstName}
      lastName={lastName}
      preferredName={preferredName}
      email={user.email ?? ''}
      onFirstNameChange={setFirstName}
      onLastNameChange={setLastName}
      onPreferredNameChange={setPreferredName}
      firstNameError={hasAttemptedSave && !firstName.trim() ? 'First name is required' : undefined}
      lastNameError={hasAttemptedSave && !lastName.trim() ? 'Last name is required' : undefined}
      phone={{
        value: phoneNumber,
        onChange: handlePhoneNumberChange,
        validation: phoneValidation,
        changed: phoneChanged,
        verifiedAt: user.phoneNumberVerifiedAt ?? null,
        hasSavedNumber: Boolean(user.phoneNumber),
        onVerify: () => setShowVerificationModal(true),
      }}
      address={{
        apiKey,
        defaultValue: primaryResidence?.formatted ?? '',
        onSelect: setAddress,
        onInputChange: handleAddressInputChange,
        error: hasAttemptedSave && !addressValid ? 'Address is required' : undefined,
      }}
      verification={{
        isOpen: showVerificationModal,
        phoneNumber: user.phoneNumber ?? '',
        onClose: () => setShowVerificationModal(false),
        onVerified: () => setShowVerificationModal(false),
      }}
      deletion={{
        isModalOpen: showDeleteModal,
        isDeleting: deleteLoading,
        error: deleteError ?? undefined,
        onOpenModal: () => {
          setDeleteError(null);
          setShowDeleteModal(true);
        },
        onCloseModal: () => setShowDeleteModal(false),
        onConfirm: () => void handleDeleteAccount(),
      }}
      isSaving={isSaving}
      isSaveDisabled={!hasChanges || !isFormValid || isSaving}
      justSaved={justSaved}
      saveError={saveError ?? undefined}
      onSave={() => void handleSave()}
    />
  );
};

PersonalSettingsForm.displayName = 'PersonalSettingsForm';

export default PersonalSettingsForm;
